import { AsyncPipe, DatePipe, NgClass, NgFor, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Optional, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SidebarModule } from 'primeng/sidebar';
import { DialogModule } from 'primeng/dialog';
import { ElectronService } from '../../../services/electron.service';
import { combineLatest, Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import {
  globalRulesCloseShiftFail,
  globalRulesCloseShiftSuccess,
  globalRulesGetCloseShiftOrderSummary,
  globalRulesLoadElectronApplicationVersions,
  selectGlobalRulesElectronApplicationVersions,
  selectGlobalRulesPosUiVersion,
  selectGlobalRulesCloseShiftOrderSummary,
  selectGlobalRulesIsCheckShiftLoading,
  selectGlobalRulesIsShiftOpen,
  selectGlobalRulesIsSidebarCollapsed,
  selectGlobalRulesIsWrongShift,
  selectGlobalRulesOpenShiftTime,
  closeSidebar,
} from '../../../stores/global';
import { Store } from '@ngrx/store';
import { AppAuthService } from '../../../cores/auth/app-auth-service';
import { CloseShiftOrderSummary } from '../../../models/nested/close-shift-order-summary';
import { environment } from '../../../../environments/environment';
import { PERMISSIONS } from '../../../cores/permissions';
import { HasPermissionDirective } from '../../../cores/permissions/has-permission.directive';
import { MessageService } from 'primeng/api';
import { Actions, ofType } from '@ngrx/effects';
import { LanguageInitService, PosLanguageCode } from '../../../cores/i18n';
import { ElectronSystemInfo } from '../../../models/electron';
import {
  ElectronApplicationVersions,
  WebRuntimeInfo,
} from '../../../models/application-version';
import { ApplicationVersionService } from '../../../services/application-version.service';
import { LocalStorageService } from '../../../services/local-storage.service';

const SIDEBAR_CLOSE_OVERLAY_DELAY_MS = 300;

interface SidebarItem {
  labelKey: string;
  icon: string;
  route: string;
  id: string;
  badge: number | null;
  isReadyOnProduction: boolean;
  permission: string;
}

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.css'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SidebarModule,
    RouterLink,
    RouterLinkActive,
    ButtonModule,
    OverlayPanelModule,
    AsyncPipe,
    NgFor,
    NgIf,
    NgClass,
    DatePipe,
    HasPermissionDirective,
    DialogModule,
    TranslateModule,
    FormsModule,
    SelectButtonModule,
  ],
})
export class SidebarComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private shouldOpenDeactivationAfterCloseShift = false;
  trackByRoute(index: number, item: SidebarItem): string { return item.route; }
  permissions = PERMISSIONS;
  @Output() openCloseShiftEmit = new EventEmitter<boolean>();
  @Output() openStartShiftEmit = new EventEmitter<boolean>();
  @Output() openDeactivationEmit = new EventEmitter<void>();
  sidebarVisible: boolean = false;
  showOpenShiftDialog: boolean = false;
  showCloseShiftDialog: boolean = false;
  isWrongShift: Observable<boolean>;
  isShiftOpen: Observable<boolean>;
  isShiftLoading: Observable<boolean>;
  openShiftTime: Observable<string | null>;
  closeShiftOrderSummary: Observable<CloseShiftOrderSummary | null>;
  closeShiftOrderSummaryData: CloseShiftOrderSummary | null = null;
  posUiVersion: Observable<string>;
  electronApplicationVersions: Observable<ElectronApplicationVersions>;
  systemInfo: ElectronSystemInfo | null = null;
  webRuntimeInfo: WebRuntimeInfo;
  posCode: string | null = null;
  storeCode: string | null = null;
  shiftCode: string | null = null;
  userName: string | null = null;

  showAboutDialog: boolean = false;
  selectedLanguage: PosLanguageCode = 'vi';
  languageOptions = [
    { label: 'VI', value: 'vi' as PosLanguageCode },
    { label: 'EN', value: 'en' as PosLanguageCode },
  ];

  formatShiftStart(value: string): string {
    const start = new Date(value);
    if (isNaN(start.getTime())) {
      return '';
    }
    const pad = (n: number) => String(n).padStart(2, '0');
    const time = `${pad(start.getHours())}:${pad(start.getMinutes())}`;
    const now = new Date();
    const isToday =
      start.getFullYear() === now.getFullYear() &&
      start.getMonth() === now.getMonth() &&
      start.getDate() === now.getDate();
    return isToday ? time : `${pad(start.getDate())}/${pad(start.getMonth() + 1)} ${time}`;
  }

  get isElectron(): boolean {
    return this.electronService.isElectron;
  }

  sidebarItems: SidebarItem[] = [
    {
      labelKey: 'nav.point_of_sale',
      icon: 'pi pi-box',
      route: '/point-of-sale',
      id: 'nav-pos',
      badge: null,
      isReadyOnProduction: environment.SUPPORTED_FEATURES.POINT_OF_SALE,
      permission: PERMISSIONS.POINT_OF_SALE.VIEW
    },
    {
      labelKey: 'nav.order_management',
      icon: 'pi pi-shopping-cart',
      route: '/order-management',
      id: 'nav-orders',
      badge: null,
      isReadyOnProduction: environment.SUPPORTED_FEATURES.ORDER_MANAGEMENT,
      permission: PERMISSIONS.ORDER_MANAGEMENT.VIEW
    },
    {
      labelKey: 'nav.system_logs',
      icon: 'pi pi-history',
      route: '/system-logs',
      id: 'nav-system-logs',
      badge: null,
      isReadyOnProduction: environment.SUPPORTED_FEATURES.SYSTEM_LOGS,
      permission: PERMISSIONS.SYSTEM_LOGS.VIEW
    }
  ];

  constructor(
    private store: Store,
    private authService: AppAuthService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private languageInitService: LanguageInitService,
    private translateService: TranslateService,
    private electronService: ElectronService,
    private localStorageService: LocalStorageService,
    private applicationVersionService: ApplicationVersionService,
    @Optional() private actions$: Actions | null,
    @Optional() private messageService: MessageService | null,
  ) {
    this.selectedLanguage = this.languageInitService.getCurrentLanguage();
    this.store.select(selectGlobalRulesIsSidebarCollapsed)
      .pipe(takeUntil(this.destroy$))
      .subscribe(collapsed => {
        this.sidebarVisible = collapsed;
        if (collapsed) {
          this.refreshTerminalInfo();
        }
        this.cdr.markForCheck();
      });
    this.isWrongShift = this.store.select(selectGlobalRulesIsWrongShift);
    this.isShiftOpen = this.store.select(selectGlobalRulesIsShiftOpen);
    this.openShiftTime = this.store.select(selectGlobalRulesOpenShiftTime);
    this.isShiftOpen.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.refreshTerminalInfo();
      this.cdr.markForCheck();
    });
    this.actions$
      ?.pipe(
        ofType(globalRulesCloseShiftSuccess, globalRulesCloseShiftFail),
        takeUntil(this.destroy$),
      )
      .subscribe((action) => {
        if (!this.shouldOpenDeactivationAfterCloseShift) {
          return;
        }

        this.shouldOpenDeactivationAfterCloseShift = false;

        if (action.type === globalRulesCloseShiftSuccess.type) {
          this.runAfterSidebarOverlayCloses(() => this.openDeactivationEmit.emit());
        }
      });
    this.isShiftLoading = this.store.select(
      selectGlobalRulesIsCheckShiftLoading,
    );
    this.closeShiftOrderSummary = this.store.select(
      selectGlobalRulesCloseShiftOrderSummary,
    );
    this.posUiVersion = this.store.select(selectGlobalRulesPosUiVersion);
    this.electronApplicationVersions = this.store.select(
      selectGlobalRulesElectronApplicationVersions,
    );
    this.webRuntimeInfo = this.applicationVersionService.getWebRuntimeInfo();
    this.closeShiftOrderSummary
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.closeShiftOrderSummaryData = data;
        this.cdr.markForCheck();
      });

    this.translateService.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        this.selectedLanguage = event.lang?.startsWith('en') ? 'en' : 'vi';
        this.cdr.markForCheck();
      });
  }

  ngOnInit() {}

  private refreshTerminalInfo() {
    const terminal = this.localStorageService.getTerminal();
    this.posCode = terminal?.code || null;
    this.storeCode = terminal?.channelShopCode || null;
    this.shiftCode = terminal?.shiftId != null ? String(terminal.shiftId) : null;
    this.userName = this.authService.getLoggedInName() || null;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async onLanguageChange(language: PosLanguageCode) {
    if (!language || language === this.languageInitService.getCurrentLanguage()) {
      return;
    }
    await this.languageInitService.changeLanguage(language);
    this.selectedLanguage = language;
    this.cdr.markForCheck();
  }

  onSidebarVisibleChange() {
    this.store.dispatch(closeSidebar());
  }

  private closeSidebarOverlay() {
    this.store.dispatch(closeSidebar());
    this.sidebarVisible = false;
    this.cdr.markForCheck();
  }

  private runAfterSidebarOverlayCloses(action: () => void) {
    setTimeout(action, SIDEBAR_CLOSE_OVERLAY_DELAY_MS);
  }

  onCloseShift(openDeactivationAfterCloseShift: boolean = false) {
    this.shouldOpenDeactivationAfterCloseShift = openDeactivationAfterCloseShift;
    this.store.dispatch(globalRulesGetCloseShiftOrderSummary());
    this.showCloseShiftDialog = true;
    this.openCloseShiftEmit.emit(true);
  }

  onStartShift() {
    this.showOpenShiftDialog = true;
    this.openStartShiftEmit.emit(true);
  }

  onLogout() {
    this.authService.logout();
    this.closeSidebarOverlay();
    this.router.navigate(['/login']);
  }

  onDeactivate() {
    if (!this.isElectron) {
      return;
    }

    this.closeSidebarOverlay();

    combineLatest([this.isWrongShift, this.isShiftOpen])
      .pipe(take(1))
      .subscribe(([isWrongShift, isShiftOpen]) => {
        if (!isShiftOpen) {
          this.shouldOpenDeactivationAfterCloseShift = false;
          this.runAfterSidebarOverlayCloses(() => this.openDeactivationEmit.emit());
          return;
        }

        this.messageService?.add({
          severity: 'warn',
          summary: this.translateService.instant('deactivation.requires_close_shift_summary'),
          detail: isWrongShift
            ? this.translateService.instant('deactivation.blocked_by_wrong_shift_detail')
            : this.translateService.instant('deactivation.requires_close_shift_detail'),
        });

        if (!isWrongShift) {
          this.runAfterSidebarOverlayCloses(() => this.onCloseShift(true));
          return;
        }

        this.shouldOpenDeactivationAfterCloseShift = false;
      });
  }

  onSettings() {
    if (!this.isElectron) {
      return;
    }

    this.closeSidebarOverlay();
    this.router.navigate(['/settings']);
  }

  async onAbout() {
    this.showAboutDialog = true;

    if (!this.isElectron) {
      return;
    }

    this.store.dispatch(globalRulesLoadElectronApplicationVersions());

    if (this.systemInfo) {
      return;
    }

    try {
      this.systemInfo = await this.electronService.getSystemInfo();
    } catch {
      this.systemInfo = null;
    }
    this.cdr.markForCheck();
  }
}
